// src/components/blocks/BaseBlock.jsx
import React, { useContext, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import { BaseBlockProps } from '../../../../../../types/shared/props';
import getParentBlock from '../../../../../../utils/getParentBlock';
import { DeckContext } from '../../context/DeckContext';
import BlockType from '../../../../../../enums/blockType';
import BLOCK_GAP from '../../../../../../constants/blockGap';

const BaseBlock: React.FC<BaseBlockProps> = ({
	block,
	onUpdateBlock,
	onSelect,
	setIsDragging,
	disabled,
	children,
	onDragStopCustom,
	nodeRef, // Accept nodeRef as a prop
}) => {
	const { state, dispatch } = useContext(DeckContext);
	const { scaleFactor, zoomScale } = state;
	const dragStartPos = useRef({ x: 0, y: 0 });
	const hasMoved = useRef(false);

	useEffect(() => {
		dispatch({
			type: 'UPDATE_BLOCK',
			payload: {
				...block,
				ref: nodeRef,
			},
		});
	}, [nodeRef]);

	// Watch for position changes
	useEffect(() => {
		console.log('[BaseBlock useEffect] Block position changed:', {
			blockId: block.id,
			blockLabel: block.label,
			position: block.position,
			timestamp: Date.now(),
		});
	}, [block.position?.x, block.position?.y]);

	const handleStart = (e, data) => {
		console.log('[BaseBlock handleStart] DRAG START:', {
			blockId: block.id,
			blockLabel: block.label,
			startPosition: { x: data.x, y: data.y },
			blockPosition: block.position,
			timestamp: Date.now(),
		});
		dragStartPos.current = { x: data.x, y: data.y };
		hasMoved.current = false;
		setIsDragging(true);
		console.log('[BaseBlock handleStart] setIsDragging(true) called');
	};

	const handleDrag = (e, data) => {
		// Track if the block has actually moved
		const moveDistance = Math.sqrt(
			Math.pow(data.x - dragStartPos.current.x, 2) +
				Math.pow(data.y - dragStartPos.current.y, 2)
		);

		if (moveDistance > 2) {
			hasMoved.current = true;
		}
	};

	const handleStop = (e: React.MouseEvent, data: { x: number; y: number }) => {
		console.log('[BaseBlock handleStop] ====== DRAG STOP ======', {
			blockId: block.id,
			blockLabel: block.label,
			oldPosition: block.position,
			finalPosition: { x: data.x, y: data.y },
			hasCustomStop: !!onDragStopCustom,
			timestamp: Date.now(),
		});

		const isTouchEvent =
			e.type === 'touchend' || (e.nativeEvent && 'touches' in e.nativeEvent);
		const dragThreshold = isTouchEvent ? 15 : 5;

		const dragDistance = Math.sqrt(
			Math.pow(data.x - dragStartPos.current.x, 2) +
				Math.pow(data.y - dragStartPos.current.y, 2)
		);

		if (!hasMoved.current) {
			if (state.selectedEdge?.blockId) {
				console.log(
					'[BaseBlock handleStop] Edge is selected, not opening block modal'
				);
				setIsDragging(false);
				return;
			}

			dispatch({ type: 'SELECT_BLOCK', payload: block });
			dispatch({ type: 'OPEN_BLOCK_MODAL', payload: block });
			setIsDragging(false);
			return;
		}

		console.log(
			'[BaseBlock handleStop] DRAG DETECTED (moved) - Not opening modal'
		);

		setIsDragging(false);

		const xChange = data.x - (block.position?.x || 0);
		const yChange = data.y - (block.position?.y || 0);

		const updatedBlock = {
			...block,
			position: { x: data.x, y: data.y },
			...(block.landings?.length && {
				landings: block.landings.map((landing) => ({
					...landing,
					position: {
						x: (landing.position?.x || 0) + xChange,
						y: (landing.position?.y || 0) + yChange,
					},
				})),
			}),
			...(block.stairs?.length && {
				stairs: block.stairs.map((stair) => ({
					...stair,
					position: {
						x: (stair.position?.x || 0) + xChange,
						y: (stair.position?.y || 0) + yChange,
					},
				})),
			}),
			...(block.bumpouts?.length && {
				bumpouts: block.bumpouts.map((bumpout) => ({
					...bumpout,
					position: {
						x: (bumpout.position?.x || 0) + xChange,
						y: (bumpout.position?.y || 0) + yChange,
					},
				})),
			}),
		};

		if (onDragStopCustom) {
			onDragStopCustom(updatedBlock);
		} else {
			onUpdateBlock(updatedBlock);
		}
	};

	const { parentBlock } = getParentBlock(state.blocks, block.parentBlock);

	let bounds: any = {};

	if (block.type == BlockType.DECKBOX) {
		if (state.canvasBounds) {
			const { width, height, offsetX, offsetY } = state.canvasBounds;
			const blockWidth = block.displayDimensions.width * scaleFactor;
			const blockHeight = block.displayDimensions.height * scaleFactor;

			bounds = {
				left: -offsetX,
				top: -offsetY,
				right: width - offsetX - blockWidth,
				bottom: height - offsetY - blockHeight,
			};
		} else {
			// console.log('[BaseBlock] No canvas bounds available, bounds disabled');
			bounds = false;
		}
	} else if (block.type == BlockType.BUMPOUT) {
		bounds = {
			left: parentBlock.position?.x || 0,
			top: parentBlock.position?.y || 0,
			right:
				(parentBlock.position?.x || 0) +
				parentBlock.displayDimensions.width * scaleFactor -
				block.displayDimensions.width * scaleFactor,

			bottom:
				(parentBlock.position?.y || 0) +
				parentBlock.displayDimensions.height * scaleFactor -
				block.displayDimensions.height * scaleFactor,
		};
	} else if (block.type == BlockType.LANDING || block.type == BlockType.STAIR) {
		bounds = {
			left:
				(parentBlock.position?.x || 0) -
				block.displayDimensions.width * scaleFactor -
				BLOCK_GAP,
			top:
				(parentBlock.position?.y || 0) -
				block.displayDimensions.height * scaleFactor -
				BLOCK_GAP,
			right:
				(parentBlock.position?.x || 0) +
				parentBlock.displayDimensions.width * scaleFactor +
				BLOCK_GAP,
			bottom:
				(parentBlock.position?.y || 0) +
				parentBlock.displayDimensions.height * scaleFactor +
				BLOCK_GAP,
		};
	}

	const positionKey = `${block.id}-${block.position?.x}-${block.position?.y}`;

	return (
		<Draggable
			key={positionKey}
			defaultPosition={block.position || { x: 0, y: 0 }}
			onStart={handleStart}
			onDrag={handleDrag}
			onStop={handleStop}
			bounds={bounds}
			disabled={disabled}
			nodeRef={nodeRef}
			scale={zoomScale || 1}
		>
			{React.cloneElement(children, {
				ref: nodeRef,
				style: { ...children.props.style, position: 'absolute' },
			})}
		</Draggable>
	);
};

export default BaseBlock;
